import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Sparkles,
  Info,
  Loader2,
  ChefHat,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { staffService } from '../../services/staffService';
import { toast } from 'sonner';

// ---- date helpers (calendar-day safe; no UTC drift) ----
const ISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const parseISO = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const StaffMenuCalendar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDate = searchParams.get('date');
  const [cursor, setCursor] = useState(initialDate ? parseISO(initialDate) : new Date());
  const [menusByDate, setMenusByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [drawerDate, setDrawerDate] = useState(initialDate);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const monthStart = useMemo(() => startOfMonth(cursor), [cursor]);
  const monthEnd = useMemo(() => endOfMonth(cursor), [cursor]);

  const loadMonth = useCallback(async () => {
    setLoading(true);
    try {
      const startISO = ISO(monthStart);
      const endISO = ISO(monthEnd);
      const items = await staffService.getMenuCalendarRange(startISO, endISO);
      const map = {};
      for (const m of items || []) map[m.date] = m;
      setMenusByDate(map);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load menu calendar');
    } finally {
      setLoading(false);
    }
  }, [monthStart, monthEnd]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  const openDrawer = async (dateStr) => {
    setDrawerDate(dateStr);
    setSearchParams({ date: dateStr }, { replace: true });
    setDrawerLoading(true);
    setDrawerData(null);
    try {
      const data = await staffService.getMenuCalendarDay(dateStr);
      setDrawerData(data);
    } catch (e) {
      if (e?.response?.status === 404) {
        setDrawerData({ notFound: true });
      } else {
        toast.error('Failed to load menu details');
      }
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerDate(null);
    setDrawerData(null);
    const next = new URLSearchParams(searchParams);
    next.delete('date');
    setSearchParams(next, { replace: true });
  };

  // Build the 6-week grid.
  const cells = useMemo(() => {
    const firstDay = new Date(monthStart);
    const leading = firstDay.getDay(); // 0=Sun
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - leading);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      days.push(d);
    }
    return days;
  }, [monthStart]);

  const todayISO = ISO(new Date());
  const monthLabel = cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6" data-testid="staff-menu-calendar">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-orange-500" />
            Menu Calendar
          </h1>
          <p className="text-gray-600 mt-1">
            Published menus you'll prep for. Read-only — admins schedule these.
          </p>
        </div>
      </div>

      {/* Explainer banner */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
        <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Menu-day specials only.</p>
          <p className="text-amber-800">
            This view shows what admins have <span className="font-semibold">explicitly scheduled</span> for each date.
            Standing weekly items are handled through Production Forecast.
          </p>
        </div>
      </div>

      {/* Month grid */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg">{monthLabel}</CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCursor(addMonths(cursor, -1))}
              data-testid="staff-menu-prev-month"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCursor(new Date())}
              data-testid="staff-menu-today-btn"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCursor(addMonths(cursor, 1))}
              data-testid="staff-menu-next-month"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {WEEKDAY_LABELS.map((w) => (
                <div key={w} className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center pb-2">
                  {w}
                </div>
              ))}
              {cells.map((d, i) => {
                const iso = ISO(d);
                const inMonth = d.getMonth() === cursor.getMonth();
                const isToday = iso === todayISO;
                const menu = menusByDate[iso];
                const hasMenu = Boolean(menu);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => hasMenu && openDrawer(iso)}
                    disabled={!hasMenu}
                    data-testid={`staff-menu-cell-${iso}`}
                    className={[
                      'min-h-[72px] sm:min-h-[92px] p-1.5 sm:p-2 rounded-lg text-left border transition',
                      inMonth ? 'bg-white' : 'bg-slate-50',
                      hasMenu ? 'border-orange-300 hover:border-orange-500 hover:bg-orange-50 cursor-pointer' : 'border-slate-200 cursor-default',
                      isToday && 'ring-2 ring-orange-500',
                    ].filter(Boolean).join(' ')}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs sm:text-sm font-semibold ${inMonth ? 'text-gray-900' : 'text-gray-400'}`}>
                        {d.getDate()}
                      </span>
                      {hasMenu && (
                        <Sparkles className="h-3.5 w-3.5 text-orange-500" />
                      )}
                    </div>
                    {hasMenu && (
                      <div className="mt-1 space-y-0.5">
                        <p className="text-[11px] sm:text-xs font-medium text-gray-800 line-clamp-2 leading-tight">
                          {menu.title || 'Scheduled menu'}
                        </p>
                        <p className="text-[10px] sm:text-[11px] text-orange-700">
                          {menu.product_count} {menu.product_count === 1 ? 'special' : 'specials'}
                        </p>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming list (mobile-first fallback) */}
      <UpcomingList onOpen={openDrawer} />

      {/* Day drawer */}
      <Sheet open={Boolean(drawerDate)} onOpenChange={(v) => !v && closeDrawer()}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="staff-menu-day-drawer">
          <SheetHeader className="pb-2">
            <SheetTitle className="flex items-center gap-2">
              <ChefHat className="h-5 w-5 text-orange-500" />
              {drawerDate && parseISO(drawerDate).toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
              })}
            </SheetTitle>
            {drawerData?.title && !drawerData.notFound && (
              <SheetDescription className="text-sm text-gray-700">
                {drawerData.title}
              </SheetDescription>
            )}
          </SheetHeader>
          {drawerLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            </div>
          ) : drawerData?.notFound ? (
            <div className="py-12 text-center text-gray-500">
              <p>No published menu for this date.</p>
            </div>
          ) : drawerData ? (
            <div className="space-y-4 mt-4">
              <div className="flex flex-wrap items-center gap-2">
                {drawerData.mode === 'exclusive' ? (
                  <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Exclusive menu</Badge>
                ) : (
                  <Badge variant="secondary">Additive menu</Badge>
                )}
                {drawerData.delivery_override && (
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Off-schedule delivery day</Badge>
                )}
              </div>
              {drawerData.notes && (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 whitespace-pre-wrap">
                  {drawerData.notes}
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  {drawerData.product_count} {drawerData.product_count === 1 ? 'menu special' : 'menu specials'}
                </p>
                {drawerData.products?.length ? (
                  <ul className="space-y-2" data-testid="staff-menu-day-products">
                    {drawerData.products.map((p) => (
                      <li
                        key={p.product_id}
                        className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 bg-white"
                        data-testid={`staff-menu-product-${p.product_id}`}
                      >
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt=""
                            className="h-12 w-12 rounded-md object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-md bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <ChefHat className="h-5 w-5 text-orange-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{p.name}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {p.category && (
                              <span className="text-xs text-gray-500 capitalize">{p.category}</span>
                            )}
                            {p.qty_limit ? (
                              <span className="text-xs text-orange-700">
                                Cap: {p.qty_committed}/{p.qty_limit}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center" data-testid="staff-menu-day-empty">
                    <ChefHat className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 font-medium">No menu-day specials</p>
                    <p className="text-xs text-slate-500 mt-1">
                      This day is scheduled but has no specials listed. Standing weekly items still apply.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// -------- Upcoming quick-list --------
const UpcomingList = ({ onOpen }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await staffService.getMenuCalendarUpcoming(14);
        setItems(data || []);
      } catch {
        // silent — banner covers it
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (!items.length) return null;

  return (
    <Card data-testid="staff-menu-upcoming">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Next 14 days</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((m) => {
          const d = parseISO(m.date);
          const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
          return (
            <button
              type="button"
              key={m.date}
              onClick={() => onOpen(m.date)}
              className="w-full flex items-center justify-between gap-3 rounded-lg border border-slate-200 hover:border-orange-400 hover:bg-orange-50 px-3 py-2 text-left transition"
              data-testid={`staff-menu-upcoming-row-${m.date}`}
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">{m.title || 'Scheduled menu'}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
              <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 flex-shrink-0">
                {m.product_count} {m.product_count === 1 ? 'special' : 'specials'}
              </Badge>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default StaffMenuCalendar;
